import { Component, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalComponent, NzModalFooterDirective, NzModalRef } from 'ng-zorro-antd/modal';
import { BrandTitleModel, CountryModel, HxAuthService, HxBrandService, SaveBrandTitleRequest } from 'hx-services';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzUploadChangeParam, NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { buildGooglePublicUrl, getBase64 } from '@adm-web-app/utils';
import { HttpClient } from '@angular/common/http';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxCountrySelectComponent, HxLabelEditComponent } from 'hx-components';
import { NgFor, NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { MarkdownComponent } from 'ngx-markdown';
import { NzTagComponent } from 'ng-zorro-antd/tag';

interface PreviewFile {
  fileName?: string;
  link?: string;
  data?: string;
  image?: string;
  visible?: boolean;
}

@Component({
  selector: 'hc-title-edit-modal',
  templateUrl: './title-edit.modal.html',
  styleUrls: ['./title-edit.modal.less'],
  imports: [TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxCountrySelectComponent, HxLabelEditComponent, NgIf, NzAlertComponent, NzDividerComponent, NzSkeletonComponent, NzUploadComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzModalComponent, NzInputDirective, MarkdownComponent, NgFor, NzTagComponent, NzModalFooterDirective, TranslocoPipe]
})
export class BrandTitleEditModal implements OnInit {
  @Input() brandId!: number;
  @Input() brandTitleId?: number;
  form!: UntypedFormGroup;
  isLoading = {
    save: false,
    files: false,
  };
  preview: PreviewFile = {};
  country?: CountryModel;
  fileList: NzUploadFile[] = [];
  filesUploadUrl!: string;
  requiredFiles: { fileName: string, size: string, format: string, exist: boolean }[] = [];

  constructor(
    public modalRef: NzModalRef,
    private fb: UntypedFormBuilder,
    private brandService: HxBrandService,
    private tr: TranslocoService,
    private nt: NzNotificationService,
    private auth: HxAuthService,
    private http: HttpClient,
  ) {
  }

  fileUploadHeadersFn = () => {
    const token = this.auth.keycloakToken();
    return {
      Authorization: `Bearer ${token}`,
      'ngsw-bypass': 'true'
    };
  };

  handlePreviewFn = (file: NzUploadFile) => {
    const showFileFn = () => {
      this.preview = {};
      this.preview.image = file.url ?? file['preview'];
      this.preview.visible = true;
      this.preview.fileName = file.filename;
      if (this.preview.fileName?.toLowerCase().endsWith('.md') && file.url) {
        this.preview.image = undefined;
        this.http.get(file.url, {responseType: 'text'}).subscribe(response => {
          if (typeof response === 'string') {
            this.preview.data = response;
          }
        });
      }
      this.preview.link = buildGooglePublicUrl(`brand/${this.brandId}/${this.country?.domain}/${file.name}`);
    };
    if (!file.url && !file['preview'] && file.originFileObj) {
      getBase64(file.originFileObj)
        .then(result => {
          file['preview'] = result;
          showFileFn();
        });
    } else {
      showFileFn();
    }
  };
  fileRemoveFn: (file: NzUploadFile) => Observable<boolean> = file => {
    if (!file.name) {
      return of(false);
    }
    if (this.brandTitleId) {
      return from(this.brandService.removeFile(this.brandTitleId, file.name).then(() => true));
    }
    return of(false);
  };

  beforeUploadFn = (file: NzUploadFile) => {
    const filenames = this.requiredFiles.filter(r => !r.exist).map(f => f.fileName + '.' + f.format);
    return filenames.includes(file.name ?? '');
  };

  async ngOnInit(): Promise<void> {
    this.filesUploadUrl = this.brandService.getFileUploadUrl(this.brandId);
    this.form = this.fb.group({
      brandId: [this.brandId, [Validators.required]],
      countryId: [null, [Validators.required]],
      title: [null, [Validators.required]],
    });
    if (this.brandTitleId) {
      const result = await this.brandService.getBrandTitleById(this.brandId);
      this.country = result.country;
      this.form.patchValue({
        brandId: result.brand?.id,
        countryId: result.country?.id,
        title: result.title,
      });
      const files = await this.brandService.getBrandImageUrls(result.id);
      const country = result.country;
      this.fileList = files.filter(file => file.size !== '0').map(file => ({
        uid: file.id,
        size: Number(file.size),
        name: file.name?.replace(`brand/${this.brandId}/${country?.domain}/`, ''),
        filename: file.name?.replace(`brand/${this.brandId}/${country?.domain}/`, ''),
        url: file.mediaLink,
        type: file.contentType,
        status: 'done',
      } as NzUploadFile));
      this.fillRequiredFiles();
    }
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      this.form.disable();
      const model: SaveBrandTitleRequest = {...this.form.getRawValue(), id: this.brandTitleId};
      this.isLoading.save = true;
      try {
        await this.brandService.saveBrandTitle(model);
        this.form.markAsPristine();
        this.nt.success(this.tr.translate('brands.form.brandTitles'), this.tr.translate('nt.dataSaved'));
        this.modalRef.close();
      } finally {
        this.form.enable();
        this.isLoading.save = false;
      }
    }
  }

  onCountryChanged(country?: CountryModel | CountryModel[]) {
    if (!Array.isArray(country)) {
      this.country = country;
    }
  }

  handleFileUploadStatusChanged(event: NzUploadChangeParam, type: 'preview' | 'files') {
    console.log('handleFileUploadStatusChanged: ', type, event);
    if (event.type === 'success' || event.type === 'removed') {
      this.fileList = event.fileList;
      this.form.markAsDirty();
      this.fillRequiredFiles();
    }
  }

  private fillRequiredFiles() {
    if (this.fileList.length) {
      const filenames = this.fileList.map(file => file.name);
      this.requiredFiles = [
        {fileName: `logo-mobile`, size: '51x51', format: 'svg', exist: filenames.includes(`logo-mobile.svg`)},
        {fileName: `logo-black`, size: '110x43', format: 'svg', exist: filenames.includes(`logo-black.svg`)},
        {fileName: `logo-gray`, size: '1528x689', format: 'svg', exist: filenames.includes(`logo-gray.svg`)},
        {fileName: `logo`, size: '1528x689', format: 'svg', exist: filenames.includes(`logo.svg`)}
      ];
    } else {
      this.requiredFiles = [
        {fileName: `logo-mobile`, size: '51x51', format: 'svg', exist: false},
        {fileName: `logo-black`, size: '110x43', format: 'svg', exist: false},
        {fileName: `logo-gray`, size: '1528x689', format: 'svg', exist: false},
        {fileName: `logo`, size: '1528x689', format: 'svg', exist: false}
      ];
    }
  }
}
